<template>
  <section v-if="hasContent" class="lead-flow-overview-section">
    <h3>Travel</h3>
    <div v-if="countryName" class="lead-flow-travel-details__country">
      <span
        v-if="countryCode"
        :class="`fflag ff-sm fflag-${countryCode}`"
        class="lead-flow-travel-details__flag"
        :aria-label="countryName"
      ></span>
      <img v-else src="@/assets/img/globe-icon.svg" alt="">
      <strong :title="countryName">{{ countryName }}</strong>
    </div>
    <dl>
      <div v-if="activities.length" class="lead-flow-travel-details__activities-row">
        <dt>Activities</dt>
        <dd>
          <div class="lead-flow-travel-details__activities">
            <LeadActivityIcons design="new" :activities="activities" :max-visible="4" />
            <AppStatusBadge
              v-for="activity in unknownActivities"
              :key="activity.code"
              :title="activity.name"
              design="new"
              tone="neutral"
              class="lead-flow-travel-details__unknown"
              :label="activity.name"
            />
          </div>
        </dd>
      </div>
      <div v-if="partyTotal !== null" class="lead-flow-travel-details__party-row">
        <dt>Party</dt>
        <dd :title="partyLabel">
          <div class="lead-flow-travel-details__party">
            <span>{{ partyTotal }} pax{{ partyGroups.length ? ' =' : '' }}</span>
            <span
              v-for="group in partyGroups"
              :key="group.code"
              :aria-label="`${group.count} ${group.label}`"
              :title="group.label"
              class="lead-flow-travel-details__party-group"
            >
              <img :src="group.icon" alt="">
              <strong>{{ group.count }}</strong>
            </span>
          </div>
        </dd>
      </div>
      <div v-if="childAgesValue">
        <dt>Kids ages</dt>
        <dd :title="childAgesValue">{{ childAgesValue }}</dd>
      </div>
      <div v-if="travelDatesValue">
        <dt>Travel Dates</dt>
        <dd :title="travelDatesValue">{{ travelDatesValue }}</dd>
      </div>
      <div v-else-if="travelDate">
        <dt>Travel date</dt>
        <dd :title="travelDate">{{ travelDate }}</dd>
      </div>
      <div v-if="currencyCode" class="lead-flow-travel-details__currency-row">
        <dt>Currency</dt>
        <dd>
          <span class="lead-flow-travel-details__currency">
            <img :src="CurrencyIcon" alt="">
            <span>{{ currencyCode }}</span>
          </span>
        </dd>
      </div>
      <div v-for="item in additionalDetails" :key="item.code">
        <dt>{{ item.label }}</dt>
        <dd :title="item.value">{{ item.value }}</dd>
      </div>
    </dl>
  </section>
</template>

<script>
import AdultIcon from '@/assets/img/adult-icon.svg'
import ChildIcon from '@/assets/img/child-icon.svg'
import CurrencyIcon from '@/assets/img/modal-usd-icon.svg'
import TeenagerIcon from '@/assets/img/teenager-icon.svg'
import LeadActivityIcons from '@/views/crm/task-tracker/LeadActivityIcons.vue'
import { formatTravelDates, formatTravelMonth } from '@/views/crm/task-tracker/leadFlowDate'

const KNOWN_ACTIVITY_CODES = ['climbing', 'safari', 'day_trip', 'day_trips', 'island', 'island_vacation']

export default {
  name: 'LeadTravelDetails',
  data() {
    return {
      CurrencyIcon
    }
  },
  components: {
    LeadActivityIcons
  },
  props: {
    country: {
      type: Object,
      default: null
    },
    activities: {
      type: Array,
      default: () => []
    },
    party: {
      type: Object,
      default: () => ({})
    },
    travel: {
      type: Object,
      default: () => ({})
    },
    travelDates: {
      type: String,
      default: ''
    },
    childAges: {
      type: String,
      default: ''
    },
    currency: {
      type: Object,
      default: null
    }
  },
  computed: {
    countryCode() {
      return this.country && this.country.code ? this.country.code : ''
    },
    countryName() {
      return this.country && this.country.name ? this.country.name : ''
    },
    unknownActivities() {
      return this.activities.reduce((result, activity, index) => {
        const code = typeof activity === 'string' ? activity : activity && activity.code
        if (!code || KNOWN_ACTIVITY_CODES.includes(code)) return result
        const name = typeof activity === 'object' && activity.name ? activity.name : code.replace(/_/g, ' ').replace(/^\w/, letter => letter.toUpperCase())
        result.push({ code: `${code}-${index}`, name })
        return result
      }, [])
    },
    partyTotal() {
      return this.numberValue(this.party.total)
    },
    partyGroups() {
      return [
        { code: 'adult', label: 'adults', count: this.numberValue(this.party.adults), icon: AdultIcon },
        { code: 'teenager', label: 'teenagers', count: this.numberValue(this.party.teenagers), icon: TeenagerIcon },
        { code: 'child', label: 'children', count: this.numberValue(this.party.children), icon: ChildIcon }
      ].filter(group => group.count > 0)
    },
    partyLabel() {
      if (this.partyTotal === null) return ''
      const details = this.partyGroups.map(group => `${group.count} ${group.label}`)
      return `${this.partyTotal} pax${details.length ? ` · ${details.join(' · ')}` : ''}`
    },
    travelDate() {
      const travelMonth = formatTravelMonth(this.travel.date)
      if (travelMonth) return travelMonth
      const planningPeriod = this.travel.planning_period
      return planningPeriod && planningPeriod.name ? planningPeriod.name : ''
    },
    travelDatesValue() {
      return formatTravelDates(this.travelDates)
    },
    childAgesValue() {
      return this.displayValue(this.childAges)
    },
    currencyCode() {
      return this.currency && this.currency.code ? this.currency.code : ''
    },
    additionalDetails() {
      return [
        { code: 'group_type', label: 'Group type', value: this.travel.group_type },
        { code: 'climb_type', label: 'Climb type', value: this.travel.climb_type },
        { code: 'hotel_stars', label: 'Accomodation Level', value: this.travel.hotel_stars },
        { code: 'safari_duration', label: 'Safari duration', value: this.travel.safari_duration },
        { code: 'kilimanjaro_duration', label: 'Kilimanjaro duration', value: this.travel.kilimanjaro_duration },
        { code: 'beach_duration', label: 'Beach duration', value: this.travel.beach_duration },
        { code: 'other_duration', label: 'Zanzibar Duration', value: this.travel.other_duration },
        { code: 'trip_price', label: 'Budget per person', value: this.travel.trip_price }
      ].map(item => ({ ...item, value: this.displayValue(item.value) }))
        .filter(item => item.value)
    },
    hasContent() {
      return Boolean(
        this.countryName ||
        this.activities.length ||
        this.partyTotal !== null ||
        this.travelDate ||
        this.travelDatesValue ||
        this.childAgesValue ||
        this.currencyCode ||
        this.additionalDetails.length
      )
    }
  },
  methods: {
    displayValue(value) {
      if (value === null || value === undefined) return ''
      return String(value).trim()
    },
    numberValue(value) {
      if (value === '' || value === null || value === undefined) return null
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    }
  }
}
</script>

<style scoped>
.lead-flow-travel-details__country {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  margin-bottom: 13px;
}

.lead-flow-travel-details__country img {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  filter: grayscale(1);
  opacity: .55;
}

.lead-flow-travel-details__country strong {
  overflow: hidden;
  color: #26393b;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-flow-travel-details__flag {
  width: 23px;
  height: 14px;
  flex: 0 0 auto;
}

.lead-flow-travel-details__activities {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
}

.lead-flow-travel-details__activities-row {
  align-items: center;
}

.lead-flow-travel-details__party-row {
  align-items: center;
}

.lead-flow-travel-details__currency-row {
  align-items: center;
}

.lead-flow-travel-details__currency {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.lead-flow-travel-details__currency img {
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
}

.lead-flow-travel-details__party,
.lead-flow-travel-details__party-group {
  display: flex;
  align-items: center;
}

.lead-flow-travel-details__party {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 9px;
}

.lead-flow-travel-details__party-group {
  align-items: flex-end;
  gap: 4px;
}

.lead-flow-travel-details__party-group img {
  width: auto;
  max-height: 21px;
}

.lead-flow-travel-details__activities :deep(.lead-activity-icons) {
  justify-content: flex-start;
}

.lead-flow-travel-details__unknown {
  display: inline-flex;
  max-width: 140px;
  min-height: 24px;
  align-items: center;
  overflow: hidden;
  padding: 3px 8px;
  border-radius: 12px;
  background: #edf0ec;
  color: #526365;
  font-size: 10px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
